import { emailAccountSecurity } from "@shared/emailTransportSecurity";
import { useMutation, useQuery } from '@tanstack/react-query';
import { EmailAccount, InsertEmailAccount } from '@shared/schema';
import { apiRequest, queryClient } from '@/lib/queryClient';
import { useToast } from '@/hooks/use-toast';
import { operationalUiErrorMessage } from '@/lib/operationalUiError';
import {
  buildManualEmailReceivePayload,
  type ManualEmailReceiveFormInput,
  type ManualEmailReceiveResult,
} from '@/lib/emailCanonicalSend';

export function toCanonicalAccountPayload(accountData: any) {
  const password = accountData.credentials?.password
    ?? accountData.password
    ?? accountData.imapPassword
    ?? accountData.smtpPassword;
  const accessToken = accountData.credentials?.accessToken ?? accountData.accessToken;
  const refreshToken = accountData.credentials?.refreshToken ?? accountData.refreshToken;

  return {
    email: accountData.email,
    displayName: accountData.displayName ?? accountData.display_name ?? accountData.name ?? accountData.email,
    provider: accountData.provider ?? 'imap',
    protocol: accountData.protocol ?? 'imap_smtp',
    imapHost: accountData.imapHost ?? accountData.imap_host,
    imapPort: Number(accountData.imapPort ?? accountData.imap_port),
    imapSecure: emailAccountSecurity(accountData).imapSecure,
    smtpHost: accountData.smtpHost ?? accountData.smtp_host,
    smtpPort: Number(accountData.smtpPort ?? accountData.smtp_port),
    smtpSecure: emailAccountSecurity(accountData).smtpSecure,
    username: accountData.username ?? accountData.imapUsername ?? accountData.email,
    credentials: {
      ...(password ? { password } : {}),
      ...(accessToken ? { accessToken } : {}),
      ...(refreshToken ? { refreshToken } : {}),
    },
    isPrimary: accountData.isPrimary ?? accountData.is_primary ?? false,
    syncEnabled: accountData.syncEnabled ?? accountData.sync_enabled ?? true,
    contactPromotionEnabled: accountData.contactPromotionEnabled ?? false,
    leadGenerationEnabled: accountData.leadGenerationEnabled ?? false,
    whiteCodeDetectionEnabled: accountData.whiteCodeDetectionEnabled ?? false,
    aiAnalysisEnabled: accountData.aiAnalysisEnabled ?? false,
  };
}

export const useEmailAccounts = () => {
  const result = useQuery({
    queryKey: ['/api/email/accounts'],
    queryFn: async () => {
      try {
        const authToken = typeof window !== 'undefined' ? localStorage.getItem('auth_token') : null;
        const response = await fetch('/api/email/accounts', {
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            ...(authToken ? { Authorization: `Bearer ${authToken}` } : {}),
          },
        });

        if (!response.ok) {
          if (response.status === 503) {
            return [] as unknown as EmailAccount[];
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const data = await response.json();

        // I dati arrivano direttamente come array dal backend
        const accounts = Array.isArray(data) ? data : [];

        // Mappa sia il contratto canonico camelCase sia i campi legacy ancora letti dalla UI.
        const mappedAccounts = accounts.map(account => {
          const status = account.status || (account.isActive || account.is_active ? 'connected' : 'inactive');
          const isActive = account.isActive ?? account.is_active ?? ['active', 'connected'].includes(status);

          return {
            id: account.id,
            email: account.email,
            username: account.username || account.email,
            displayName: account.displayName || account.display_name || account.username || account.email,
            isActive,
            isPrimary: account.isPrimary ?? account.is_primary ?? false,
            syncEnabled: account.syncEnabled ?? account.sync_enabled ?? true,
            status,
            lastSyncTime: account.lastSyncTime || account.last_sync_time,
            lastError: account.lastError || account.last_error,
            emailCount: account.emailCount || 0,
            imapHost: account.imapHost || account.imap_host || account.config?.imapHost,
            imapPort: account.imapPort || account.imap_port || account.config?.imapPort,
            smtpHost: account.smtpHost || account.smtp_host || account.config?.smtpHost,
            smtpPort: account.smtpPort || account.smtp_port || account.config?.smtpPort,
            hasCredentials: account.hasCredentials ?? false,
            config: account.config || {
              imapHost: account.imapHost || account.imap_host,
              imapPort: account.imapPort || account.imap_port,
              smtpHost: account.smtpHost || account.smtp_host,
              smtpPort: account.smtpPort || account.smtp_port,
            }
          };
        });

        return mappedAccounts as unknown as EmailAccount[];
      } catch (error) {
        if (error instanceof Error && error.message.includes('HTTP 503')) {
          return [] as unknown as EmailAccount[];
        }
        console.error('[useEmailAccounts] Errore nel recupero degli account email:', error);
        throw error;
      }
    },
    staleTime: 5000,
    retry: 1,
    refetchOnWindowFocus: false,
    refetchInterval: false
  });

  return {
    ...result,
    emailAccounts: result.data || []
  };
};

export const useEmailAccount = (id: number | null) => {
  // Utilizziamo la cache degli account già caricati quando possibile
  const { emailAccounts, isLoading: isLoadingAccounts } = useEmailAccounts();

  return useQuery<EmailAccount | undefined>({
    queryKey: ['/api/email/accounts', id],
    queryFn: async () => {
      if (!id) return undefined;

      // Verifica se possiamo trovare l'account nella cache degli account già caricati
      const cachedAccount = emailAccounts.find(acc => acc.id === id);
      if (cachedAccount) {
        return cachedAccount;
      }

      // Se non è nella cache, fai una richiesta diretta
      try {
        const response = await apiRequest('GET', `/api/email/accounts/${id}`);
        return response as EmailAccount;
      } catch (error) {
        console.error(`Errore nel recupero dell'account ${id}:`, error);
        throw error;
      }
    },
    // Abilitiamo la query solo se c'è un ID e non abbiamo già l'account nella cache
    enabled: !!id && !emailAccounts.some(acc => acc.id === id),
    // Utilizziamo un tempo di stale più breve per questo dato specifico
    staleTime: 30000, // 30 secondi
  });
};

export const useDeleteEmailAccount = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: number) => {
      return apiRequest('POST', `/api/email/accounts/${id}/disable`);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/accounts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts/status'] });
      queryClient.removeQueries({ queryKey: ['/api/email/accounts', data.id] });

      toast({
        title: 'Account disattivato',
        description: "L'account email è stato disattivato con successo.",
      });
    },
    onError: (error) => {
      console.error('[useDeleteEmailAccount] Errore eliminazione account email:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile eliminare l\'account email. Controlla i log per maggiori dettagli.',
        variant: 'destructive',
      });
    },
  });
};

export const useManualEmailReceiveTest = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, input }: { id: number; input: ManualEmailReceiveFormInput }) => {
      const payload = buildManualEmailReceivePayload(input);
      return apiRequest('POST', `/api/email/accounts/${id}/receive-test`, payload) as Promise<ManualEmailReceiveResult>;
    },
    onSuccess: (result) => {
      toast({
        title: 'Verifica ricezione completata',
        description: `Finestra controllata: ${result.matched} match, ${result.imported} importate, checkpoint sync invariato.`,
      });
      queryClient.invalidateQueries({ queryKey: ['/api/emails'] });
    },
    onError: (error) => {
      console.error('[useManualEmailReceiveTest] Errore verifica ricezione manuale:', error);
      toast({
        title: 'Verifica ricezione fallita',
        description: operationalUiErrorMessage(error, 'Impossibile completare la verifica ricezione.'),
        variant: 'destructive',
      });
    },
  });
};

export interface EmailAutoconfigResult {
  imapHost: string;
  imapPort: number;
  imapSecure: boolean;
  smtpHost: string;
  smtpPort: number;
  smtpSecure: boolean;
  usernameIsFullEmail: boolean;
  found: boolean;
  source: 'static' | 'ispdb' | 'ispdb-mx' | 'guess';
  providerLabel?: string;
}

/**
 * Rileva automaticamente host/porta IMAP+SMTP dal dominio dell'indirizzo
 * email (provider noti, poi Mozilla ISPDB, poi MX, poi best-effort).
 */
export const useEmailAutoconfig = () => {
  return useMutation({
    mutationFn: async (email: string): Promise<EmailAutoconfigResult> => {
      return apiRequest('GET', `/api/email/accounts/autoconfig?email=${encodeURIComponent(email)}`) as Promise<EmailAutoconfigResult>;
    },
  });
};

export interface EmailConnectionProbe {
  protocol: 'imap' | 'smtp';
  success: boolean;
  diagnosticCode: string;
}

export interface EmailConnectionTest {
  imap: EmailConnectionProbe;
  smtp: EmailConnectionProbe;
}

/**
 * Semaforo: testa IMAP+SMTP sui dati del form senza salvare.
 * Ritorna { imap, smtp } con success true/false e un diagnosticCode.
 */
export const useVerifyEmailConnection = () => {
  return useMutation({
    mutationFn: async (accountData: any): Promise<EmailConnectionTest> => {
      const payload = toCanonicalAccountPayload(accountData);
      return apiRequest('POST', '/api/email/accounts/verify-connection', {
        provider: payload.provider,
        protocol: payload.protocol,
        imapHost: payload.imapHost,
        imapPort: payload.imapPort,
        imapSecure: payload.imapSecure,
        smtpHost: payload.smtpHost,
        smtpPort: payload.smtpPort,
        smtpSecure: payload.smtpSecure,
        username: payload.username,
        credentials: payload.credentials,
      }) as Promise<EmailConnectionTest>;
    },
  });
};

export const useCreateEmailAccount = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (accountData: InsertEmailAccount) => {
      return apiRequest('POST', '/api/email/accounts', toCanonicalAccountPayload(accountData));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/accounts'] });
      queryClient.invalidateQueries({ queryKey: ['/api/email-accounts/status'] });
      toast({
        title: 'Account creato',
        description: 'Account email aggiunto con successo.',
      });
    },
    onError: (error) => {
      console.error('Failed to create email account:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile creare account email. Riprova.',
        variant: 'destructive',
      });
    },
  });
};

export const useUpdateEmailAccount = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, accountData }: { id: number; accountData: Partial<InsertEmailAccount> }) => {
      return apiRequest('PATCH', `/api/email/accounts/${id}`, accountData);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/accounts'] });
      toast({
        title: 'Account aggiornato',
        description: 'Account email aggiornato con successo.',
      });
    },
    onError: (error) => {
      console.error('Failed to update email account:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile aggiornare l\'account email. Riprova.',
        variant: 'destructive',
      });
    },
  });
};

export const useSetPrimaryEmailAccount = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async (id: number) => {
      return apiRequest('POST', `/api/email/accounts/${id}/primary`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/accounts'] });
      toast({
        title: 'Account principale impostato',
        description: 'Account email principale aggiornato.',
      });
    },
    onError: (error) => {
      console.error('Failed to set primary account:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile impostare l\'account principale. Riprova.',
        variant: 'destructive',
      });
    },
  });
};

export const useToggleEmailAccountActive = () => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, isActive }: { id: number; isActive: boolean }) => {
      return apiRequest('POST', `/api/email/accounts/${id}/${isActive ? 'restore' : 'disable'}`);
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['/api/email/accounts'] });
      toast({
        title: variables.isActive ? 'Account abilitato' : 'Account disabilitato',
        description: `Account email ${variables.isActive ? 'abilitato' : 'disabilitato'} con successo.`,
      });
    },
    onError: (error) => {
      console.error('Failed to toggle account status:', error);
      toast({
        title: 'Errore',
        description: 'Impossibile aggiornare lo stato dell\'account. Riprova.',
        variant: 'destructive',
      });
    },
  });
};

export const useAccountStatusColor = (status: string) => {
  switch (status) {
    case 'ok':
      return 'bg-primary';
    case 'error':
      return 'bg-red-500';
    case 'warning':
      return 'bg-amber-500';
    default:
      return 'bg-muted';
  }
};
